import { useState } from 'react';

const read = <T>(key: string | null): T | undefined => {
	if (key === null) return undefined;
	try {
		const raw = globalThis.localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : undefined;
	} catch {
		return undefined;
	}
};

// stylovyze's useLocalStorage reads once per mount, so a key that changes
// afterwards (model, user or tenant resolving late) would show stale data.
// Same JSON format, so both hooks can read the same entries.
export function useKeyedLocalStorage<T>(
	key: string | null,
): [T | undefined, (value: T) => void] {
	const [state, setState] = useState(() => ({ key, value: read<T>(key) }));
	let current = state;
	if (state.key !== key) {
		current = { key, value: read<T>(key) };
		setState(current);
	}
	const setValue = (value: T) => {
		// With a half-built key (userId, tenantId or configId not yet resolved)
		// the change applies in memory only; storage takes over once it resolves.
		if (key !== null) {
			try {
				globalThis.localStorage.setItem(key, JSON.stringify(value));
			} catch {
				// storage unavailable or full: keep the in-memory value only
			}
		}
		setState({ key, value });
	};
	return [current.value, setValue];
}
